/**
 * Admin Layout Component - Detachable Admin Module
 * 
 * Clean, minimal admin layout matching the main application design.
 * When extracting to a separate app, this component can be moved with minimal changes.
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Settings, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import BillingConfigModal from './BillingConfigModal';

interface AdminLayoutProps {
  children: React.ReactNode;
}

/**
 * Clean, minimal admin layout matching the main application design
 */
export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, switchRole, isAdminOnly } = useAuth();
  
  // Billing configuration modal state
  const [showBillingConfig, setShowBillingConfig] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSwitchToUser = () => {
    switchRole('user');
    navigate('/dashboard');
  };

  // No navigation items needed - single page admin interface

  // Header component matching the main app design
  const Header = () => (
    <div className="flex items-center justify-between px-8 py-4 border-b bg-white">
      <div className="flex items-center space-x-6">
        <div className="flex items-center space-x-3">
          <div className="font-bold text-blue-700 text-lg leading-tight">BlueGenAI Admin</div>
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        {!isAdminOnly && (
          <button
            onClick={handleSwitchToUser}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors flex items-center space-x-2"
            title="Switch to User View"
          >
            <User className="h-4 w-4" />
            <span>User View</span>
          </button>
        )}
        <button
          onClick={() => setShowBillingConfig(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors flex items-center space-x-2"
          title="Setup Billing Configuration"
        >
          <Settings className="h-4 w-4" />
          <span>Billing Config</span>
        </button>
        <button
          onClick={handleLogout}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
        >
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="max-w-6xl w-full mx-auto h-full flex flex-col min-h-0">
          <div className="flex-1 pr-1 pb-10 pt-10">
            {children}
          </div>
        </div>
      </div>
      
      {/* Billing Configuration Modal */}
      <BillingConfigModal
        isOpen={showBillingConfig}
        onClose={() => setShowBillingConfig(false)}
        onSuccess={() => {
          console.log('Billing configuration updated successfully');
        }}
      />
    </div>
  );
};

export default AdminLayout;